import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import { SERVICES } from "../lib/services";
import ServiceIcon from "../components/ServiceIcon";
import api from "../lib/api";
import { ArrowRight, ShieldCheck, Zap, Eye, HeartHandshake, Sparkles, Calendar, Newspaper } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1658724685009-0514a6ee5958?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjBjdXN0b21lciUyMGRpZ2l0YWwlMjB0YWJsZXR8ZW58MHx8fHwxNzg1Mjk1NDUzfDA&ixlib=rb-4.1.0&q=85";

const Home = () => {
    const { t, lang } = useApp();
    const featured = SERVICES.slice(0, 9);
    const [news, setNews] = useState([]);

    useEffect(() => {
        api.get("/news").then(({ data }) => setNews(data.slice(0, 3))).catch(() => {});
    }, []);

    return (
        <div data-testid="page-home">
            {/* HERO */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 -z-10 decorative-grid opacity-40 dark:opacity-20" />
                <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[hsl(var(--gold))]/10 blur-3xl -z-10" />
                <div className="absolute -bottom-32 -left-24 w-[420px] h-[420px] rounded-full bg-[hsl(var(--royal))]/10 blur-3xl -z-10" />

                <div className="container-x pt-14 sm:pt-20 pb-16 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7 fade-up">
                        <div className="chip bg-[hsl(var(--gold))]/15 text-[hsl(var(--royal-deep))] dark:text-[hsl(var(--gold-light))] mb-6">
                            <Sparkles size={14} /> {t.hero.tagline}
                        </div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
                            {t.hero.title1}<br />
                            <span className="gradient-text-gold">{t.hero.title2}</span><br />
                            <span className="text-[hsl(var(--royal))]">{t.hero.title3}</span>
                        </h1>
                        <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">
                            {t.hero.desc}
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link data-testid="hero-book-cta" to="/booking" className="btn-royal">{t.hero.cta1} <ArrowRight size={16} /></Link>
                            <Link data-testid="hero-track-cta" to="/track" className="btn-ghost-royal">{t.hero.cta2}</Link>
                        </div>
                        <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
                            {[t.hero.stat1, t.hero.stat2, t.hero.stat3].map((s, i) => (
                                <div key={i} className="glass-card rounded-2xl px-4 py-3">
                                    <div className="text-xs font-semibold text-[hsl(var(--royal))] dark:text-[hsl(var(--gold-light))]">{s}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="lg:col-span-5 fade-up delay-200">
                        <div className="relative">
                            <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-tr from-[hsl(var(--royal))]/30 to-[hsl(var(--gold))]/30 blur-2xl" />
                            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl border-4 border-white/40 dark:border-white/10">
                                <img src={HERO_IMG} alt="Digital service" className="w-full h-[420px] sm:h-[520px] object-cover" />
                                <div className="absolute top-4 right-4 w-24 h-24 rounded-full bg-white p-2 shadow-2xl border-2 border-[hsl(var(--gold))]/40">
                                    <img src="/logo.webp" alt="Sri Matha Multiservices Logo" className="w-full h-full object-contain" />
                                </div>
                                <div className="absolute bottom-4 left-4 right-4 glass-card rounded-2xl p-4 flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-[hsl(var(--gold))] flex items-center justify-center text-slate-900"><ShieldCheck size={20} /></div>
                                    <div className="text-sm">
                                        <div className="font-semibold">MeeSeva Authorized · Est. 2009</div>
                                        <div className="text-xs text-muted-foreground">Govt. of Telangana</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* SERVICES PREVIEW */}
            <section className="section-pad bg-muted/40">
                <div className="container-x">
                    <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
                        <div className="max-w-2xl">
                            <div className="chip bg-[hsl(var(--royal))]/10 text-[hsl(var(--royal))] mb-3">{t.nav.services}</div>
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">{t.home.servicesTitle}</h2>
                            <p className="mt-3 text-muted-foreground">{t.home.servicesDesc}</p>
                        </div>
                        <Link to="/services" data-testid="home-view-all" className="btn-ghost-royal !py-2 !px-4">{t.home.viewAll} <ArrowRight size={16} /></Link>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {featured.map((s, i) => (
                            <Link key={s.id} to={`/booking?service=${s.id}`} data-testid={`service-tile-${s.id}`}
                                className={`service-tile group fade-up delay-${(i % 5) * 100}`}>
                                <div className="flex items-start justify-between mb-4">
                                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-[hsl(var(--royal))]/10 text-[hsl(var(--royal))] group-hover:bg-[hsl(var(--gold))]/20 group-hover:text-[hsl(var(--royal-deep))] transition-colors">
                                        <ServiceIcon name={s.icon} size={22} />
                                    </div>
                                    <span className="chip bg-muted text-muted-foreground">{s.category}</span>
                                </div>
                                <h3 className="text-lg font-semibold tracking-tight">{s.name[lang]}</h3>
                                <p className="mt-1 text-sm text-muted-foreground">{s.desc[lang]}</p>
                                <div className="mt-4 text-sm font-semibold text-[hsl(var(--royal))] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                                    {t.services.book} <ArrowRight size={14} />
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY */}
            <section className="section-pad">
                <div className="container-x grid lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-5">
                        <div className="chip bg-[hsl(var(--gold))]/15 text-[hsl(var(--royal-deep))] dark:text-[hsl(var(--gold-light))] mb-3">Why us</div>
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">{t.home.whyTitle}</h2>
                        <p className="mt-3 text-muted-foreground">{t.home.whyDesc}</p>
                    </div>
                    <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
                        {[
                            { Icon: ShieldCheck, t: t.home.f1t, d: t.home.f1d },
                            { Icon: Zap, t: t.home.f2t, d: t.home.f2d },
                            { Icon: Eye, t: t.home.f3t, d: t.home.f3d },
                            { Icon: HeartHandshake, t: t.home.f4t, d: t.home.f4d },
                        ].map(({ Icon, t: title, d }, i) => (
                            <div key={i} className="p-6 rounded-2xl border border-border bg-card hover:-translate-y-1 hover:shadow-lg transition-transform duration-200">
                                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                                    style={{ background: "linear-gradient(135deg, hsl(var(--royal))/15%, hsl(var(--gold))/15%)" }}>
                                    <Icon size={22} className="text-[hsl(var(--royal))]" />
                                </div>
                                <h3 className="text-base font-semibold">{title}</h3>
                                <p className="mt-1 text-sm text-muted-foreground">{d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* NEWS */}
            {news.length > 0 && (
                <section className="pb-24">
                    <div className="container-x">
                        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
                            <div className="max-w-2xl">
                                <div className="chip bg-[hsl(var(--gold))]/15 text-[hsl(var(--royal-deep))] dark:text-[hsl(var(--gold-light))] mb-3">
                                    <Newspaper size={14} /> {t.home.newsTitle}
                                </div>
                                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">{t.home.newsTitle}</h2>
                                <p className="mt-3 text-muted-foreground">{t.home.newsDesc}</p>
                            </div>
                            <Link to="/news" data-testid="home-view-news" className="btn-ghost-royal !py-2 !px-4">{t.home.viewAllNews} <ArrowRight size={16} /></Link>
                        </div>
                        <div className="grid md:grid-cols-3 gap-5">
                            {news.map((n) => (
                                <Link key={n.id} to="/news" data-testid={`home-news-${n.id}`}
                                    className="group rounded-3xl border border-border bg-card overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-transform duration-200">
                                    {n.image_url && (
                                        <div className="aspect-[16/10] overflow-hidden bg-muted">
                                            <img src={n.image_url} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                        </div>
                                    )}
                                    <div className="p-5">
                                        <div className="flex items-center gap-3 text-xs">
                                            <span className="px-2.5 py-1 rounded-full font-semibold bg-[hsl(var(--royal))]/10 text-[hsl(var(--royal))]">{n.category}</span>
                                            <span className="text-muted-foreground inline-flex items-center gap-1"><Calendar size={12} /> {new Date(n.published_at).toLocaleDateString()}</span>
                                        </div>
                                        <h3 className="mt-2 text-base font-semibold tracking-tight leading-snug line-clamp-2">{n.title}</h3>
                                        <p className="mt-1.5 text-sm text-muted-foreground line-clamp-2">{n.summary}</p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* CTA */}
            <section className="pb-24">
                <div className="container-x">
                    <div className="relative overflow-hidden rounded-3xl p-10 sm:p-16 text-center noise"
                        style={{ background: "linear-gradient(135deg, hsl(var(--royal)) 0%, hsl(var(--royal-deep)) 100%)" }}>
                        <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-[hsl(var(--gold))]/20 blur-3xl" />
                        <h3 className="relative text-2xl sm:text-4xl font-bold text-white tracking-tight">{t.home.ctaTitle}</h3>
                        <p className="relative mt-3 text-slate-200">{t.home.ctaDesc}</p>
                        <div className="relative mt-8 flex flex-wrap justify-center gap-3">
                            <Link data-testid="cta-book-btn" to="/booking" className="btn-gold">{t.hero.cta1} <ArrowRight size={16} /></Link>
                            <a data-testid="cta-wa-btn" href="https://wa.me/919000767404" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white border-2 border-white/40 hover:bg-white/10">
                                {t.common.whatsapp}
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
